import { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, Grid, Typography, Alert,
  FormControlLabel, Checkbox
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DropZone from '../common/DropZone';
import { uploadSingleImage } from '../../services/upload.service';

const galleriesSchema = z.object({
  title: z.string().min(1, 'Укажите название'),
  description: z.string().optional(),
  coverImageUrl: z.string().optional(), // Uploaded via DropZone
  isPublic: z.boolean().default(false),
});

type GalleriesFormValues = z.infer<typeof galleriesSchema>;

interface GalleriesFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: GalleriesFormValues) => Promise<void>;
  initialData?: GalleriesFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function GalleriesForm({ open, onClose, onSubmit, initialData, loading, error }: GalleriesFormProps) {
  const [coverUploading, setCoverUploading] = useState(false);
  
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset,
    setValue,
    watch,
  } = useForm<GalleriesFormValues>({
    resolver: zodResolver(galleriesSchema),
    defaultValues: { 
      title: '', 
      description: '', 
      coverImageUrl: '', 
      isPublic: false 
    },
  });

  const coverImageUrl = watch('coverImageUrl');

  useEffect(() => {
    if (initialData) reset(initialData);
    else reset({ 
      title: '', 
      description: '', 
      coverImageUrl: '', 
      isPublic: false 
    });
  }, [initialData, open, reset]);

  // Handle cover image upload via DropZone
  const handleCoverUpload = useCallback(async (files: File[]): Promise<{ url: string }[]> => {
    setCoverUploading(true);
    try {
      const results: { url: string }[] = [];
      for (const file of files) {
        const url = await uploadSingleImage(file);
        results.push({ url });
      }
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      throw new Error(message);
    } finally {
      setCoverUploading(false);
    }
  }, []);

  const handleCoverChange = useCallback((urls: string | string[]) => {
    const url = Array.isArray(urls) ? urls[0] : urls;
    setValue('coverImageUrl', url);
  }, [setValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать галерею' : 'Создать галерею'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Название *" 
                error={!!errors.title} 
                helperText={errors.title?.message} 
                {...register('title')} 
              />
            </Grid>
            
            {/* Cover Image — DropZone */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Обложка галереи
              </Typography>
              <DropZone
                onUpload={handleCoverUpload}
                type="image"
                maxFiles={1}
                maxSize={10}
                multiple={false}
                value={coverImageUrl || ''}
                onChange={handleCoverChange}
                label="Перетащите изображение или нажмите для выбора"
                helperText="JPG, PNG, WebP до 10MB"
              />
              {coverUploading && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Загрузка...
                </Typography>
              )}
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Описание" 
                multiline 
                rows={3} 
                {...register('description')} 
              />
            </Grid>
            
            {/* isPublic */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('isPublic')} />}
                label="Галерея доступна публично"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button 
          onClick={handleSubmit(onSubmit)} 
          variant="contained" 
          disabled={loading || coverUploading}
        >
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}